import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import { TipoDomicilioArray } from "../../../Arrays/TipoDomicilioArray";
import { SexoArray } from "../../../Arrays/SexoArray";
import { EstadoCivilArray } from "../../../Arrays/EstadoCivilArray";
import { RegimenConyugalArray } from "../../../Arrays/RegimenConyugalArray";
import { CRM_LeyendaAuditoria } from "./CRM_LeyendaAuditoria";
import { CRM_LeyendaDepartamento } from "./CRM_LeyendaDepartamento";

const DOMICILIO_VACIO = {
  direccion: "",
  cp: "",
  idestado: "",
  estado: "",
  idmunicipio: "",
  municipio: "",
  idlocalidad: "",
  localidad: "",
  tipodomicilio: "",
  referencia1: "",
  referencia2: "",
  mapsUrl: "",
};

// Datos exclusivos de Persona Física (tipoPersona === "F"), capturados aparte del domicilio
const DATOS_PF_VACIO = {
  nombre: "",
  nombre2: "",
  apellidopaterno: "",
  apellidomaterno: "",
  curp: "",
  sexo: "",
  estadocivil: "",
  regimenconyugal: "",
};

// El domicilio de Buró de crédito es la dirección con orden 1 dentro de info_ubicacion_cliente —
// mismo array que consume Ubicación, pero esa sección la excluye porque solo se gestiona aquí
const mapDomicilioToForm = (d) => ({
  direccion: d?.direccion || "",
  cp: d?.cp ? String(d.cp) : "",
  idestado: d?.idestado || "",
  estado: d?.estado || "",
  idmunicipio: d?.idmunicipio || "",
  municipio: d?.municipio || "",
  idlocalidad: d?.idlocalidad || "",
  localidad: d?.localidad || "",
  tipodomicilio: d?.tipodomicilio || "",
  referencia1: d?.referencia1 || "",
  referencia2: d?.referencia2 || "",
  mapsUrl: d?.ubicacion || "",
});

// Datos de Persona Física que regresa el backend tras guardar (result.datos_persona_fisica)
const mapDatosPFToForm = (d) => ({
  nombre: d?.nombre || "",
  nombre2: d?.nombre2 || "",
  apellidopaterno: d?.apellido_paterno || "",
  apellidomaterno: d?.apellido_materno || "",
  curp: d?.curp || "",
  sexo: d?.sexo || "",
  estadocivil: d?.estado_civil || "",
  regimenconyugal: d?.regimen_conyugal || "",
});

export const CRM_DetalleBuroCredito = ({ esNuevo, idcliente, tipoPersona, ubicacionCliente = [], datosPersonaFisica = null, opcionesUbicacion = { estado: [], municipio: [] }, setLoader, onGuardado, soloLectura = false, responsableDepartamento = [] }) => {
  const empleado = useSelector((store) => store.usuario?.empleado);
  const domicilioBuro = ubicacionCliente.find((u) => u.estatus && u.orden === 1) || null;

  const valorInicial = esNuevo || !domicilioBuro ? DOMICILIO_VACIO : mapDomicilioToForm(domicilioBuro);
  const valorInicialPF = esNuevo || !datosPersonaFisica ? DATOS_PF_VACIO : mapDatosPFToForm(datosPersonaFisica);

  const [editing, setEditing] = useState(esNuevo);
  const [form, setForm] = useState(valorInicial);
  const [DatosPF, setDatosPF] = useState(valorInicialPF);
  const [auditoria, setAuditoria] = useState(domicilioBuro);
  const [localidades, setLocalidades] = useState(
    valorInicial.idlocalidad ? [{ idlocalidad: valorInicial.idlocalidad, localidad: valorInicial.localidad }] : []
  );
  const [buscando, setBuscando] = useState(false);

  // ubicacionCliente llega después del primer render (fetch async en CRM_DetalleScreen) — se sincroniza aquí
  useEffect(() => {
    if (esNuevo || !domicilioBuro) return;
    const mapeado = mapDomicilioToForm(domicilioBuro);
    setForm(mapeado);
    setAuditoria(domicilioBuro);
    setLocalidades(mapeado.idlocalidad ? [{ idlocalidad: mapeado.idlocalidad, localidad: mapeado.localidad }] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ubicacionCliente]);

  // datosPersonaFisica llega después del primer render (fetch async en CRM_DetalleScreen) — se
  // sincroniza aquí, igual que ubicacionCliente arriba; si el cliente no trae datos capturados
  // (o no es Persona Física), DatosPF se deja vacío en vez de conservar un valor obsoleto
  useEffect(() => {
    if (esNuevo) return;
    setDatosPF(datosPersonaFisica ? mapDatosPFToForm(datosPersonaFisica) : DATOS_PF_VACIO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosPersonaFisica]);

  const fieldClass = () => (editing ? "crm-field-editable" : "crm-field-readonly");
  const cpBloqueado = !!form.cp;

  const handleChangeCampo = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  const handleChangeDatosPF = (campo) => (e) => {
    setDatosPF((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  // Régimen conyugal solo aplica si el estado civil es "Casado" — al cambiar a cualquier otro
  // se limpia, igual que el patrón ya usado en Estado/Municipio de este mismo componente
  const handleChangeEstadoCivil = (e) => {
    const estadocivil = e.target.value;
    setDatosPF((prev) => ({ ...prev, estadocivil, regimenconyugal: estadocivil === "CA" ? prev.regimenconyugal : "" }));
  };

  const handleEditar = () => setEditing(true);
  const handleCancelar = () => {
    setForm(valorInicial);
    setDatosPF(valorInicialPF);
    setEditing(false);
  };

  // Busca por código postal — bloquea Estado/Municipio y resuelve sus valores cruzando los catálogos ya cargados
  const buscarPorCP = () => {
    if (!form.cp) return;
    setBuscando(true);
    request
      .get(`Clientes/GetLocalidadesCRM?codigo_postal=${form.cp}`)
      .then((result) => {
        const encontradas = result || [];
        setLocalidades(encontradas);

        const idmunicipio = encontradas[0]?.idmunicipio || "";
        const municipioEncontrado = opcionesUbicacion.municipio.find((m) => String(m.idmunicipio) === String(idmunicipio));
        const estadoEncontrado = municipioEncontrado
          ? opcionesUbicacion.estado.find((e) => String(e.idestado) === String(municipioEncontrado.idestado))
          : null;

        setForm((prev) => ({
          ...prev,
          idmunicipio,
          municipio: municipioEncontrado?.municipio || "",
          idestado: estadoEncontrado?.idestado || "",
          estado: estadoEncontrado?.estado || "",
          idlocalidad: "",
          localidad: "",
        }));
      })
      .catch((error) => Swal.fire("CLIENTES", JSON.stringify(error), "error"))
      .finally(() => setBuscando(false));
  };

  // Se borra el CP — se desbloquean Estado/Municipio para elegirlos manualmente
  const handleLimpiarCP = () => {
    setForm((prev) => ({ ...prev, cp: "", idestado: "", estado: "", idmunicipio: "", municipio: "", idlocalidad: "", localidad: "" }));
    setLocalidades([]);
  };

  const handleChangeEstado = (e) => {
    const idestado = e.target.value;
    const estadoEncontrado = opcionesUbicacion.estado.find((es) => String(es.idestado) === String(idestado));
    setForm((prev) => ({ ...prev, idestado, estado: estadoEncontrado?.estado || "", idmunicipio: "", municipio: "", idlocalidad: "", localidad: "" }));
    setLocalidades([]);
  };

  // Al elegir municipio (solo posible sin CP) se cargan sus localidades
  const handleChangeMunicipio = (e) => {
    const idmunicipio = e.target.value;
    const municipioEncontrado = opcionesUbicacion.municipio.find((m) => String(m.idmunicipio) === String(idmunicipio));
    setForm((prev) => ({ ...prev, idmunicipio, municipio: municipioEncontrado?.municipio || "", idlocalidad: "", localidad: "" }));

    if (!idmunicipio) {
      setLocalidades([]);
      return;
    }

    setBuscando(true);
    request
      .get(`Clientes/GetLocalidadesCRM?idmunicipio=${idmunicipio}`)
      .then((result) => setLocalidades(result || []))
      .catch((error) => Swal.fire("CLIENTES", JSON.stringify(error), "error"))
      .finally(() => setBuscando(false));
  };

  // Al elegir localidad se autocompleta su código postal
  const handleChangeLocalidad = (e) => {
    const idlocalidad = e.target.value;
    const encontrada = localidades.find((l) => String(l.idlocalidad) === String(idlocalidad));
    setForm((prev) => ({ ...prev, idlocalidad, localidad: encontrada?.localidad || "", cp: encontrada?.cp ? String(encontrada.cp) : prev.cp }));
  };

  const validar = () => {
    if (!form.cp || form.cp.length !== 5) {
      Swal.fire("CLIENTES", "El código postal es obligatorio y debe tener 5 dígitos", "warning");
      return false;
    }
    if (!form.idlocalidad) {
      Swal.fire("CLIENTES", "El campo localidad es obligatorio", "warning");
      return false;
    }
    if (!form.tipodomicilio) {
      Swal.fire("CLIENTES", "El campo tipo de domicilio es obligatorio", "warning");
      return false;
    }
    if (!form.direccion.trim()) {
      Swal.fire("CLIENTES", "El campo dirección es obligatorio", "warning");
      return false;
    }
    return true;
  };

  // Mismo endpoint batch que Ubicación (ClientesDomicilio) — se manda un único domicilio
  // fijo en orden 1, con buro_credito: true para distinguirlo del guardado de Ubicación, más los
  // datos de Persona Física (planos, junto a Domicilios) cuando tipoPersona === "F"
  const handleGuardar = async () => {
    if (!validar()) return;

    const body = {
      IdCliente: Number(idcliente),
      Domicilios: [{
        orden: 1,
        idlocalidad: Number(form.idlocalidad),
        direccion: form.direccion,
        tipodomicilio: form.tipodomicilio,
        principal: false,
        referencia1: form.referencia1,
        referencia2: form.referencia2,
        estatus: true,
        ubicacion: form.mapsUrl,
      }],
      buro_credito: true,
      tipoPersona: tipoPersona,
      nombre: DatosPF.nombre,
      nombre2: DatosPF.nombre2,
      apellido_paterno: DatosPF.apellidopaterno,
      apellido_materno: DatosPF.apellidomaterno,
      curp: DatosPF.curp,
      sexo: DatosPF.sexo,
      estado_civil: DatosPF.estadocivil,
      regimen_conyugal: DatosPF.regimenconyugal,
    };

    setLoader?.((prev) => ({ ...prev, mostrar: true }));
    try {
      const result = await request.post("Clientes/GuardarDatosBuroCredito", body);
      // El endpoint regresa el listado completo actualizado (igual que en Ubicación) — de ahí
      // se toma el registro de orden 1 recién guardado para refrescar la leyenda de auditoría,
      // y se sube el listado completo al padre para que Ubicación también refleje el cambio
      const actualizado = (result?.domicilios || []).find((u) => u.orden === 1);
      if (actualizado) setAuditoria(actualizado);
      if (result?.domicilios) onGuardado?.(result.domicilios);
      // Cuando tipoPersona === "F", el backend regresa también los datos de Persona Física ya
      // guardados — se refresca DatosPF con lo que confirma el servidor, igual que domicilios
      if (result?.datos_persona_fisica) setDatosPF(mapDatosPFToForm(result.datos_persona_fisica));
      setLoader?.((prev) => ({ ...prev, mostrar: false }));
      await Swal.fire("CLIENTES", "Domicilio guardado con éxito", "success");
      setEditing(false);
    } catch (error) {
      setLoader?.((prev) => ({ ...prev, mostrar: false }));
      Swal.fire("CLIENTES", JSON.stringify(error), "error");
    }
  };

  return (
    <div id="sec-buro-credito" className="crm-detalle-card" style={{ scrollMarginTop: 170 }}>
      <div className="crm-detalle-card-header crm-detalle-card-header-actions">
        <div>
          <h3>Datos para buró de crédito</h3>
          <p>Datos registrados para consulta en buró de crédito</p>
        </div>
        <div className="crm-detalle-card-header-buttons">
          {editing ? (
            <>
              {!esNuevo && (
                <button type="button" className="btn-icono-ghost" title="Cancelar" onClick={handleCancelar}>
                  <CloseIcon />
                </button>
              )}
              <button type="button" className="btn-icono-solido" title="Guardar cambios" onClick={handleGuardar}>
                <CheckIcon />
                <span>Guardar</span>
              </button>
            </>
          ) : (
            !soloLectura && (
              <button type="button" className="btn-icono-ghost btn-editar" title="Editar" onClick={handleEditar}>
                <EditIcon />
                <span>Editar</span>
              </button>
            )
          )}
        </div>
      </div>

      <div className="crm-detalle-card-body">

        {tipoPersona === "F" && (
          <>
            <div className="crm-detalle-grid crm-detalle-grid-2">
              <div className="crm-field">
                <label>Primer Nombre</label>
                <input type="text" className={fieldClass()} disabled={!editing} maxLength={50} value={DatosPF.nombre} onChange={handleChangeDatosPF("nombre")} />
              </div>
              <div className="crm-field">
                <label>Segundo Nombre</label>
                <input type="text" className={fieldClass()} disabled={!editing} maxLength={50} value={DatosPF.nombre2} onChange={handleChangeDatosPF("nombre2")} />
              </div>
            </div>

            <div className="crm-detalle-grid crm-detalle-grid-2">
              <div className="crm-field">
                <label>Apellido Paterno</label>
                <input type="text" className={fieldClass()} disabled={!editing} maxLength={50} value={DatosPF.apellidopaterno} onChange={handleChangeDatosPF("apellidopaterno")} />
              </div>
              <div className="crm-field">
                <label>Apellido Materno</label>
                <input type="text" className={fieldClass()} disabled={!editing} maxLength={50} value={DatosPF.apellidomaterno} onChange={handleChangeDatosPF("apellidomaterno")} />
              </div>
            </div>

            <div className="crm-detalle-grid crm-detalle-grid-2">
              <div className="crm-field">
                <label>CURP</label>
                <input type="text" className={`mono ${fieldClass()}`} disabled={!editing} maxLength={18} value={DatosPF.curp} onChange={handleChangeDatosPF("curp")} />
              </div>
              <div className="crm-field">
                <label>Sexo</label>
                <select className={fieldClass()} disabled={!editing} value={DatosPF.sexo} onChange={handleChangeDatosPF("sexo")}>
                  <option value="">Seleccionar</option>
                  {SexoArray.map((s) => (
                    <option key={s.id} value={s.id}>{s.display}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="crm-detalle-grid crm-detalle-grid-2">
              <div className="crm-field">
                <label>Estado Civil</label>
                <select className={fieldClass()} disabled={!editing} value={DatosPF.estadocivil} onChange={handleChangeEstadoCivil}>
                  <option value="">Seleccionar</option>
                  {EstadoCivilArray.map((ec) => (
                    <option key={ec.id} value={ec.id}>{ec.display}</option>
                  ))}
                </select>
              </div>
              <div className="crm-field">
                <label>Régimen Conyugal</label>
                <select className={fieldClass()} disabled={!editing || DatosPF.estadocivil !== "CA"} value={DatosPF.regimenconyugal} onChange={handleChangeDatosPF("regimenconyugal")}>
                  <option value="">Seleccionar</option>
                  {RegimenConyugalArray.map((rc) => (
                    <option key={rc.id} value={rc.id}>{rc.display}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        <div className="crm-field">
          <label>Dirección</label>
          <input type="text" className={fieldClass()} disabled={!editing} value={form.direccion} onChange={handleChangeCampo("direccion")} />
        </div>

        <div className="crm-detalle-grid crm-detalle-grid-4">
          <div className="crm-field">
            <label>Código postal</label>
            <div className="crm-field-inline-btn">
              <div className="crm-field-cp-wrap">
                <input
                  type="text"
                  className={`mono ${fieldClass()}`}
                  disabled={!editing}
                  value={form.cp}
                  onChange={handleChangeCampo("cp")}
                  onKeyDown={(e) => e.key === "Enter" && buscarPorCP()}
                />
                {editing && form.cp && (
                  <button type="button" className="btn-cp-limpiar" title="Quitar CP" onClick={handleLimpiarCP}>×</button>
                )}
              </div>
              {editing && (
                <button type="button" className="btn-cp-buscar" title="Buscar por CP" disabled={!form.cp || buscando} onClick={buscarPorCP}>
                  <SearchIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>
          <div className="crm-field">
            <label>Estado</label>
            <select className={fieldClass()} disabled={!editing || cpBloqueado} value={form.idestado} onChange={handleChangeEstado}>
              <option value="">Seleccionar</option>
              {opcionesUbicacion.estado.map((es) => (
                <option key={es.idestado} value={es.idestado}>{es.estado}</option>
              ))}
            </select>
          </div>
          <div className="crm-field">
            <label>Municipio</label>
            <select className={fieldClass()} disabled={!editing || cpBloqueado || !form.idestado} value={form.idmunicipio} onChange={handleChangeMunicipio}>
              <option value="">Seleccionar</option>
              {opcionesUbicacion.municipio
                .filter((m) => String(m.idestado) === String(form.idestado))
                .map((m) => (
                  <option key={m.idmunicipio} value={m.idmunicipio}>{m.municipio}</option>
                ))}
            </select>
          </div>
          <div className="crm-field">
            <label>Localidad</label>
            <select className={fieldClass()} disabled={!editing || localidades.length === 0} value={form.idlocalidad} onChange={handleChangeLocalidad}>
              <option value="">Seleccionar</option>
              {localidades.map((l) => (
                <option key={l.idlocalidad} value={l.idlocalidad}>{l.localidad}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="crm-detalle-grid crm-detalle-grid-4">
          <div className="crm-field">
            <label>Tipo de domicilio</label>
            <select className={fieldClass()} disabled={!editing} value={form.tipodomicilio} onChange={handleChangeCampo("tipodomicilio")}>
              <option value="">Seleccionar</option>
              {TipoDomicilioArray.map((t) => (
                <option key={t.id} value={t.id}>{t.display}</option>
              ))}
            </select>
          </div>
          <div className="crm-field">
            <label>Referencia 1</label>
            <input type="text" className={fieldClass()} disabled={!editing} value={form.referencia1} onChange={handleChangeCampo("referencia1")} />
          </div>
          <div className="crm-field">
            <label>Referencia 2</label>
            <input type="text" className={fieldClass()} disabled={!editing} value={form.referencia2} onChange={handleChangeCampo("referencia2")} />
          </div>
          <div className="crm-field">
            <label>Enlace de Google Maps</label>
            <input type="text" className={fieldClass()} disabled={!editing} value={form.mapsUrl} onChange={handleChangeCampo("mapsUrl")} />
          </div>
        </div>

      </div>

      <div className="crm-detalle-card-footer">
        <CRM_LeyendaDepartamento responsables={responsableDepartamento} idrel={4} />
        <CRM_LeyendaAuditoria auditoria={auditoria} />
      </div>
    </div>
  );
};
